import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { Breadcrumbs } from '../../components/Breadcrumbs';
import { LimitedFeature } from '../../components/LimitedFeature';
import { RightDrawer } from '../../components/RightDrawer';
import { JSONPretty } from '../../components/JSONPretty';
import { Tabs, TabsStrategy } from '../../components/Tabs';
import { PATH_WORKFLOWS } from '../../app/constants/paths.constant';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { CompatibleStateChips } from './CompatibleStateChips';
import { EntityWorkflows } from './EntityWorkflows';
import {
  lookupStateById,
  lookupWorkflowById,
} from './utils/build-workflow-graph.util';
import {
  extractCompatibleStateGroups,
  translateMultiplicity,
} from './utils/resolve-compatible-states.util';
import { extractCompatibleWorkflowGroups } from './utils/resolve-compatible-workflows.util';
import { extractRoleRequirements } from './utils/resolve-role-requirements.util';
import { extractSettings } from './utils/resolve-settings.util';
import type {
  WorkflowSelection,
  WorkflowStateLookup,
} from './utils/build-workflow-graph.util';
import type { CompatibleFieldConfig } from './utils/resolve-compatible-states.util';
import type { CompatibleWorkflowFieldConfig } from './utils/resolve-compatible-workflows.util';
import type { WorkflowEntry } from './types/workflow-entry.type';

const extractDescription = (raw: unknown): string | undefined => {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }

  const { description } = raw as Record<string, unknown>;

  return typeof description === 'string' ? description : undefined;
};

export const WorkflowsPage = () => {
  const { setPage } = useNavigationContext();

  useEffect(() => {
    setPage(Page.Workflows);
  }, [setPage]);

  const {
    folderWorkflows,
    missionWorkflows,
    businessEventWorkflows,
    reportWorkflows,
    quotationWorkflows,
    accountingWorkflows,
    folderStatesIdMap,
    missionStatesIdMap,
    businessEventStatesIdMap,
    reportStatesIdMap,
    quotationStatesIdMap,
    accountingStatesIdMap,
    folderWorkflowsIdMap,
    missionWorkflowsIdMap,
  } = useReferencesContext();

  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerDescription, setDrawerDescription] = useState<
    string | undefined
  >(undefined);
  const [compatibleGroups, setCompatibleGroups] =
    useState<ReturnType<typeof extractCompatibleStateGroups>>(EMPTY_ARRAY);
  const [compatibleWorkflowGroups, setCompatibleWorkflowGroups] =
    useState<ReturnType<typeof extractCompatibleWorkflowGroups>>(EMPTY_ARRAY);
  const [settings, setSettings] =
    useState<ReturnType<typeof extractSettings>>(EMPTY_ARRAY);
  const [roleRequirements, setRoleRequirements] =
    useState<ReturnType<typeof extractRoleRequirements>>(EMPTY_ARRAY);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const compatibleFieldConfigs: readonly CompatibleFieldConfig[] = useMemo(
    () => [
      {
        field: 'folder',
        entityLabel: 'Dossier',
        gender: 'masculine',
        getState: (stateId: string) =>
          lookupStateById(folderStatesIdMap, stateId),
      },
      {
        field: 'mission',
        entityLabel: 'Mission',
        gender: 'feminine',
        getState: (stateId: string) =>
          lookupStateById(missionStatesIdMap, stateId),
      },
      {
        field: 'businessEvent',
        entityLabel: 'Événement Métier',
        gender: 'masculine',
        getState: (stateId: string) =>
          lookupStateById(businessEventStatesIdMap, stateId),
      },
      {
        field: 'report',
        entityLabel: 'Rapport',
        gender: 'masculine',
        getState: (stateId: string) =>
          lookupStateById(reportStatesIdMap, stateId),
      },
      {
        field: 'quotation',
        entityLabel: 'Devis',
        gender: 'masculine',
        getState: (stateId: string) =>
          lookupStateById(quotationStatesIdMap, stateId),
      },
      {
        field: 'accounting',
        entityLabel: 'Comptabilité',
        gender: 'feminine',
        getState: (stateId: string) =>
          lookupStateById(accountingStatesIdMap, stateId),
      },
    ],
    [
      folderStatesIdMap,
      missionStatesIdMap,
      businessEventStatesIdMap,
      reportStatesIdMap,
      quotationStatesIdMap,
      accountingStatesIdMap,
    ],
  );

  const compatibleWorkflowFieldConfigs: readonly CompatibleWorkflowFieldConfig[] =
    useMemo(
      () => [
        {
          field: 'folderWorkflowIds',
          entityLabel: 'Dossier',
          getWorkflow: (id: string) =>
            lookupWorkflowById(folderWorkflowsIdMap, id),
        },
        {
          field: 'missionWorkflowIds',
          entityLabel: 'Mission',
          getWorkflow: (id: string) =>
            lookupWorkflowById(missionWorkflowsIdMap, id),
        },
      ],
      [folderWorkflowsIdMap, missionWorkflowsIdMap],
    );

  const handleSelect = useCallback(
    ({ kind, raw, title }: Readonly<WorkflowSelection>) => {
      setDrawerTitle(title);
      setDrawerDescription(extractDescription(raw));
      setCompatibleGroups(
        kind === 'node'
          ? extractCompatibleStateGroups(raw, compatibleFieldConfigs)
          : EMPTY_ARRAY,
      );
      setCompatibleWorkflowGroups(
        kind === 'workflow'
          ? extractCompatibleWorkflowGroups(raw, compatibleWorkflowFieldConfigs)
          : EMPTY_ARRAY,
      );
      setSettings(extractSettings(raw));
      setRoleRequirements(extractRoleRequirements(raw));
      setIsDrawerOpen(true);
    },
    [compatibleFieldConfigs, compatibleWorkflowFieldConfigs],
  );

  const entityTabs: readonly Readonly<{
    key: string;
    label: string;
    workflows: readonly WorkflowEntry[];
    getState: WorkflowStateLookup;
  }>[] = useMemo(
    () => [
      {
        key: 'folder',
        label: 'Dossier',
        workflows: folderWorkflows,
        getState: (stateId: string) =>
          lookupStateById(folderStatesIdMap, stateId),
      },
      {
        key: 'mission',
        label: 'Mission',
        workflows: missionWorkflows,
        getState: (stateId: string) =>
          lookupStateById(missionStatesIdMap, stateId),
      },
      {
        key: 'businessEvent',
        label: 'Événement Métier',
        workflows: businessEventWorkflows,
        getState: (stateId: string) =>
          lookupStateById(businessEventStatesIdMap, stateId),
      },
      {
        key: 'report',
        label: 'Rapport',
        workflows: reportWorkflows,
        getState: (stateId: string) =>
          lookupStateById(reportStatesIdMap, stateId),
      },
      {
        key: 'quotation',
        label: 'Devis',
        workflows: quotationWorkflows,
        getState: (stateId: string) =>
          lookupStateById(quotationStatesIdMap, stateId),
      },
      {
        key: 'accounting',
        label: 'Comptabilité',
        workflows: accountingWorkflows,
        getState: (stateId: string) =>
          lookupStateById(accountingStatesIdMap, stateId),
      },
    ],
    [
      folderWorkflows,
      missionWorkflows,
      businessEventWorkflows,
      reportWorkflows,
      quotationWorkflows,
      accountingWorkflows,
      folderStatesIdMap,
      missionStatesIdMap,
      businessEventStatesIdMap,
      reportStatesIdMap,
      quotationStatesIdMap,
      accountingStatesIdMap,
    ],
  );

  return (
    <>
      <Helmet>
        <title>{buildDocumentTitle('Workflows')}</title>
      </Helmet>
      <Breadcrumbs
        paths={[{ to: PATH_WORKFLOWS, label: 'workflows', key: 'workflows' }]}
      />
      <PageTitle title="Workflows" />
      <LimitedFeature />
      <Tabs
        id="workflows-entities"
        strategy={TabsStrategy.Stateful}
        tabs={entityTabs.map(({ key, label, workflows, getState }) => ({
          key,
          value: key,
          label,
          panel: (
            <EntityWorkflows
              getState={getState}
              label={label}
              onSelect={handleSelect}
              workflows={workflows}
            />
          ),
        }))}
        title="Type d'entité"
      />
      <RightDrawer
        isOpen={isDrawerOpen}
        onDrawerClose={handleDrawerClose}
        title={drawerTitle}
      >
        <Box sx={{ width: '100%' }}>
          {!!drawerDescription && (
            <Typography color="text.secondary" sx={{ mb: 3 }} variant="body2">
              {drawerDescription}
            </Typography>
          )}
          {!!compatibleGroups.length && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              {compatibleGroups.map(
                ({ entityLabel, gender, multiplicity, states }) => (
                  <Box key={entityLabel}>
                    <Typography sx={{ mb: 1 }} variant="subtitle2">
                      États compatibles - {entityLabel} (
                      {translateMultiplicity(multiplicity, gender)})
                    </Typography>
                    <CompatibleStateChips states={states} />
                  </Box>
                ),
              )}
            </Stack>
          )}
          {!!compatibleWorkflowGroups.length && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              {compatibleWorkflowGroups.map(({ entityLabel, workflows }) => (
                <Box key={entityLabel}>
                  <Typography sx={{ mb: 1 }} variant="subtitle2">
                    Compatibilité - {entityLabel}
                  </Typography>
                  {workflows ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: 'wrap' }}
                      useFlexGap
                    >
                      {workflows.map((workflow) => (
                        <Chip key={workflow.id} label={workflow.label} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      Aucune restriction (compatible avec tous)
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
          {!!settings.length && (
            <Stack sx={{ mb: 3 }}>
              <Typography sx={{ mb: 1 }} variant="subtitle2">
                Paramètres
              </Typography>
              {settings.map((entry) =>
                entry.kind === 'boolean' ? (
                  <FormControlLabel
                    control={<Switch checked={entry.value} disabled />}
                    key={entry.key}
                    label={entry.label}
                  />
                ) : (
                  <Box
                    key={entry.key}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 0.5,
                    }}
                  >
                    <Typography color="text.secondary" variant="body2">
                      {entry.label}
                    </Typography>
                    <Typography variant="body2">{entry.value}</Typography>
                  </Box>
                ),
              )}
            </Stack>
          )}
          {!!roleRequirements.length && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography sx={{ mb: 1 }} variant="subtitle2">
                Droits
              </Typography>
              {roleRequirements.map(({ key, label, value }) => (
                <Box key={key}>
                  <Typography color="text.secondary" variant="caption">
                    {label}
                  </Typography>
                  <JSONPretty
                    data={value}
                    id={`workflow-role-requirement-${key}`}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </RightDrawer>
    </>
  );
};
